import React from 'react';
import { Collapse, CollapseProps, Typography } from 'antd';
import { AccordionConfig } from '../../../../types/accordion';
import type { FormField } from '../../../../types/templates';
import { ConfigField } from './config-field';
import { ImageGridLayout, RegionGridLayout, isImageGrid } from './grid-layouts';
import { useResponsiveSizes } from '../../../../hooks/use-responsive-sizes';

const { Text } = Typography;

interface ConfigAccordionProps {
  config: AccordionConfig[];
  data: Record<string, unknown>;
  formFields: readonly FormField[];
  getFieldLabel: (key: string) => string;
}

export const ConfigAccordion: React.FC<ConfigAccordionProps> = ({ config, data, formFields, getFieldLabel }) => {
  const { fontSize, spacing } = useResponsiveSizes();

  const fieldByKey = React.useMemo(() => {
    const map = new Map<string, FormField>();
    formFields.forEach(f => map.set(f.key, f));
    return map;
  }, [formFields]);

  const items: CollapseProps['items'] = config.flatMap(section => {
    const sectionData: SectionEntry[] = section.fields
      .map(key => ({ key, value: data[key], field: fieldByKey.get(key) }))
      .filter(({ value }) => value !== undefined && value !== null && value !== '');

    if (sectionData.length === 0) return [];

    return [
      {
        key: section.key,
        label: (
          <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
            <div
              style={{
                width: 4,
                height: 20,
                backgroundColor: section.color,
                borderRadius: 2
              }}
            />
            <Text style={{ fontSize: fontSize.base }}>{section.title}</Text>
          </div>
        ),
        style: {
          marginBottom: spacing.sm,
          borderRadius: 16,
          border: '1px solid #f0f0f0',
          padding: '12px 5px'
        },
        children:
          section.layout === 'grid' ? (
            <GridDisplay data={sectionData} getFieldLabel={getFieldLabel} />
          ) : (
            <DefaultDisplay data={sectionData} getFieldLabel={getFieldLabel} />
          )
      }
    ];
  });

  return <Collapse style={{ backgroundColor: 'transparent' }} ghost items={items} />;
};

export type SectionEntry = { key: string; value: unknown; field: FormField | undefined };

interface DisplayProps {
  data: SectionEntry[];
  getFieldLabel: (key: string) => string;
}

const DefaultDisplay: React.FC<DisplayProps> = ({ data, getFieldLabel }) => {
  const { spacing } = useResponsiveSizes();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
      {data.map(({ key, value, field }) => (
        <ConfigField key={key} keyName={key} field={field} value={value} getFieldLabel={getFieldLabel} />
      ))}
    </div>
  );
};

const GridDisplay: React.FC<DisplayProps> = ({ data, getFieldLabel }) => {
  if (isImageGrid(data)) {
    return <ImageGridLayout data={data} getFieldLabel={getFieldLabel} />;
  }
  return <RegionGridLayout data={data} getFieldLabel={getFieldLabel} />;
};
